import {
  ConfigApi,
  createApiRef,
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { SearchQuery, SearchResultSet } from '@backstage/plugin-search-common';
import qs from 'qs';
import { searchTypes } from '../components/search/searchTypes';

export const searchApiRef = createApiRef<SearchApi>({
  id: 'plugin.search.queryservice',
});

export interface SearchApi {
  query(query: SearchQuery): Promise<SearchResultSet>;
}

export class SearchClient implements SearchApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;
  private readonly backendUrl: string | undefined;
  private readonly fetchApi: FetchApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
    config: ConfigApi;
    fetchApi: FetchApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
    this.backendUrl = options.config.getOptionalString('backend.baseUrl');
    this.fetchApi = options.fetchApi;
  }

  private async getUrls() {
    return {
      apiUrl: `${this.backendUrl}/api/sev2`,
    };
  }

  async query(query: SearchQuery): Promise<SearchResultSet> {
    const { token } = await this.identityApi.getCredentials();
    const { apiUrl } = await this.getUrls();

    // Check if the query term is a task ID (e.g., T12345)
    const taskIdPrefixRegex = /^T\d+$/;
    const isTaskId = taskIdPrefixRegex.test(query.term);

    let data;

    if (isTaskId) {
      // Call the task API
      const response = await this.fetchApi.fetch(`${apiUrl}/task.search`, {
        method: 'POST',
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          : {},
        body: JSON.stringify({
          constraints: {
            idLike: query.term,
          },
        }),
      });

      if (!response.ok) {
        throw await ResponseError.fromResponse(response);
      }

      data = await response.json();
    } else {
      // Check if the query term start with prefix 'T[space]' (e.g., T task)
      const taskPrefixRegex = /^T\s+(.*)$/; //  "T[space][term]"
      const isTaskSearch = taskPrefixRegex.test(query.term);

      const types = searchTypes
        .filter(type =>
          isTaskSearch
            ? type.value === 'task-snippets'
            : type.value !== 'task-snippets',
        )
        .map(type => type.value);

      const term = isTaskSearch
        ? query.term.replace(taskPrefixRegex, '$1')
        : query.term;

      const newQuery = {
        ...query,
        types,
        term,
      };

      const queryString = qs.stringify(newQuery, { arrayFormat: 'repeat' });

      const url = `${await this.discoveryApi.getBaseUrl(
        'search',
      )}/query?${queryString}`;

      // Call the search API
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw await ResponseError.fromResponse(response);
      }

      data = await response.json();
    }

    return data;
  }
}
